/**
 * MyStories - 我的绘本 Tab
 * 展示用户创作的绘本列表
 */
import { useState, useEffect } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Clock, Edit, Trash2, Eye, Sparkles, Globe, Lock, Heart, Play, MessageCircle } from 'lucide-react';
import { storyApi } from '../../../utils/api';
import { useAuth } from '../../../contexts/AuthContext';

interface Story {
  id: string;
  title: string;
  coverUrl?: string;
  imageUrls?: string[];
  isPublic?: boolean;
  theme?: string;
  tags?: string[];
  status: 'creating' | 'completed';
  stageLabel?: string;
  pageCount: number;
  viewCount?: number;
  likeCount?: number;
  playCount?: number;
  commentCount?: number;
  createdAt: string;
  updatedAt: string;
}

export default function MyStories() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'creating' | 'completed'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const categoryFilters = [
    { value: 'all', label: '全部主题' },
    { value: 'adventure', label: '冒险' },
    { value: 'friendship', label: '友谊' },
    { value: 'space', label: '太空' },
    { value: 'dinosaur', label: '恐龙' },
    { value: 'nature', label: '自然' },
  ];

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      fetchStories();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const fetchStories = async () => {
    try {
      const token = localStorage.getItem('token');
      const userId = user?.id;
      
      if (!token || !userId) {
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/stories/user/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        // 转换后端数据格式为前端格式
        // 后端 status: DRAFT/PENDING_REVIEW/PUBLISHED/ARCHIVED
        // 前端 status: creating/completed
        const formattedStories = data.data.map((story: any) => ({
          id: story.id,
          title: story.title,
          coverUrl: story.coverUrl || story.imageUrls?.[0],
          imageUrls: story.imageUrls,
          isPublic: story.isPublic,
          theme: story.theme,
          tags: story.tags || (story.theme ? [story.theme] : []),
          status: story.status === 'PUBLISHED' ? 'completed' : 'creating',
          pageCount: story.pageCount || story._count?.pages || 0,
          viewCount: story.viewCount || 0,
          likeCount: story.likeCount || 0,
          playCount: story.playCount || 0,
          commentCount: story.commentCount || 0,
          createdAt: story.createdAt,
          updatedAt: story.createdAt,
        }));
        setStories(formattedStories);
      }
    } catch (error) {
      console.error('Failed to fetch stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteStory = async (id: string) => {
    if (!confirm('确定要删除这本绘本吗？')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/stories/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setStories(stories.filter(s => s.id !== id));
      }
    } catch (error) {
      alert('删除失败');
    }
  };

  const toggleVisibility = async (storyId: string, isPublic: boolean) => {
    try {
      await storyApi.toggleVisibility(storyId, isPublic);
      setStories(prev => prev.map(s => s.id === storyId ? { ...s, isPublic } : s));
    } catch (err: any) {
      alert(err?.response?.data?.error || '设置失败');
    }
  };

  const filteredStories = stories.filter(story => {
    if (filter !== 'all' && story.status !== filter) return false;
    if (categoryFilter !== 'all') {
      const storyTags = story.tags || (story.theme ? [story.theme] : []);
      if (!storyTags.includes(categoryFilter)) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // 未登录提示
  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-24 h-24 mb-4 bg-slate-100 rounded-2xl flex items-center justify-center">
          <BookOpen className="w-12 h-12 text-slate-300" />
        </div>
        <h3 className="text-lg font-medium text-slate-800 mb-2">请先登录</h3>
        <p className="text-slate-500 mb-6">登录后查看您的绘本创作</p>
        <button
          onClick={() => navigate('/login')}
          className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-all px-4 py-2"
        >
          去登录
        </button>
      </div>
    );
  }

  if (stories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        {/* 图标放在文字上方空白处 */}
        <div className="w-24 h-24 mb-4 bg-slate-100 rounded-2xl flex items-center justify-center">
          <BookOpen className="w-12 h-12 text-slate-300" />
        </div>
        <h3 className="text-lg font-medium text-slate-800 mb-2">还没有创建绘本</h3>
        <p className="text-slate-500 mb-6">开始创作属于孩子的专属故事吧</p>
        <button
          onClick={() => navigate('/create')}
          className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-all px-4 py-2"
        >
          <Sparkles className="w-5 h-5" />
          开始创作
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* 筛选器 */}
      <div className="flex flex-col gap-3">
        <div className="flex gap-4">
          {[
            { id: 'all', label: '全部' },
            { id: 'creating', label: '创作中' },
            { id: 'completed', label: '已完成' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setFilter(item.id as typeof filter)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                filter === item.id
                  ? 'bg-indigo-50 text-indigo-600'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          <span className="text-sm text-slate-500 whitespace-nowrap">主题:</span>
          {categoryFilters.map((item) => (
            <button
              key={item.value}
              onClick={() => setCategoryFilter(item.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                categoryFilter === item.value
                  ? 'bg-indigo-50 text-indigo-600 border border-indigo-200'
                  : 'text-slate-600 hover:bg-slate-50 border border-transparent'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* 绘本网格 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredStories.map((story, index) => (
          <motion.div
            key={story.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="group bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow"
          >
            {/* 封面 */}
            <div 
              className="aspect-[4/3] bg-gradient-to-br from-indigo-100 to-violet-100 relative overflow-hidden cursor-pointer"
              onClick={() => navigate(story.status === 'completed' ? `/reader/${story.id}` : `/create/stage/1?storyId=${story.id}`)}
            >
              {story.coverUrl ? (
                <img 
                  src={story.coverUrl} 
                  alt={story.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <BookOpen className="w-16 h-16 text-indigo-200" />
                </div>
              )}
              
              {/* 状态标签 */}
              <div className="absolute top-3 left-3 flex flex-col gap-1">
                <span className={`px-3 py-1 rounded-lg text-xs font-medium ${
                  story.status === 'completed'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {story.status === 'completed' ? '已完成' : '创作中'}
                </span>
                {story.stageLabel && story.status !== 'completed' && (
                  <span className="px-3 py-1 rounded-lg text-xs font-medium bg-white/90 text-slate-600 backdrop-blur-sm">
                    {story.stageLabel}
                  </span>
                )}
              </div>

              {/* 公开/私有切换（仅已完成绘本） */}
              {story.status === 'completed' && (
                <div className="absolute top-3 right-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleVisibility(story.id, !story.isPublic);
                    }}
                    className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border transition-all ${
                      story.isPublic
                        ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                        : 'bg-slate-100 text-slate-600 border-slate-200'
                    }`}
                    title={story.isPublic ? '点击设为私有' : '点击设为公开'}
                  >
                    {story.isPublic ? (
                      <>
                        <Globe className="w-3 h-3" />
                        <span>公开</span>
                      </>
                    ) : (
                      <>
                        <Lock className="w-3 h-3" />
                        <span>私有</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* 信息 */}
            <div className="p-6">
              <h3 className="font-bold text-slate-800 mb-2 truncate">{story.title}</h3>
              
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <BookOpen className="w-4 h-4" />
                  {story.pageCount}页
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {new Date(story.createdAt).toLocaleDateString()}
                </span>
              </div>

              {/* 标签 */}
              {story.tags && story.tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {story.tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-600 text-xs font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* 操作按钮 */}
              <div className="mt-4 flex flex-wrap items-center justify-between gap-y-2 gap-x-2">
                <div className="flex items-center gap-1.5 text-xs text-slate-500 min-w-0">
                  <span className="flex items-center gap-0.5 shrink-0" title="阅读量">
                    <Eye className="w-3.5 h-3.5" />
                    {story.viewCount || 0}
                  </span>
                  <span className="flex items-center gap-0.5 shrink-0" title="播放量">
                    <Play className="w-3.5 h-3.5" />
                    {story.playCount || 0}
                  </span>
                  <span className="flex items-center gap-0.5 shrink-0" title="评论数">
                    <MessageCircle className="w-3.5 h-3.5" />
                    {story.commentCount || 0}
                  </span>
                  <span className="flex items-center gap-0.5 shrink-0" title="点赞量">
                    <Heart className="w-3.5 h-3.5" />
                    {story.likeCount || 0}
                  </span>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => navigate(story.status === 'completed' ? `/reader/${story.id}` : `/create/stage/1?storyId=${story.id}`)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-1 px-3 py-2 whitespace-nowrap"
                  >
                    {story.status === 'completed' ? (
                      <><Eye className="w-4 h-4 shrink-0" /> 阅读</>
                    ) : (
                      <><Edit className="w-4 h-4 shrink-0" /> 继续创作</>
                    )}
                  </button>
                  <button
                    onClick={() => deleteStory(story.id)}
                    className="bg-rose-500 hover:bg-rose-600 text-white rounded-xl transition-colors px-2 py-2 flex items-center justify-center"
                  >
                    <Trash2 className="w-4 h-4 shrink-0" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
